import React, { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import Coin from '../../components/Coin';

const Dashboard = () => {
  const { user } = useAuthStore();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalCombats: 0,
    totalBets: 0,
    totalRevenue: 0
  });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/admin/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement du tableau de bord...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Tableau de Bord Administrateur
          </h1>
          <p className="text-gray-600">
            Vue d'ensemble de la plateforme KokBatay
          </p>
        </div>

        {/* Statistiques principales */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <span className="text-blue-600 text-xl">👥</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Utilisateurs totaux</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-lg">
                <span className="text-orange-600 text-xl">🐓</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Combats totaux</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalCombats}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <span className="text-green-600 text-xl">🎲</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Paris totaux</p>
                <p className="text-2xl font-bold text-gray-900">{stats.totalBets}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <span className="text-purple-600 text-xl">💰</span>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Revenus totaux</p>
                <p className="text-2xl font-bold text-gray-900"><Coin amount={stats.totalRevenue} symbol="HTG" /></p>
              </div>
            </div>
          </div>
        </div>

        {/* Actions rapides */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Actions rapides
            </h2>
            <div className="space-y-3">
              <button
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                onClick={() => navigate('/admin/combats/new')}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">➕</span>
                  <span>Créer un nouveau combat</span>
                </div>
              </button>
              <button
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                onClick={() => navigate('/admin/users')}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">👤</span>
                  <span>Gérer les utilisateurs</span>
                </div>
              </button>
              <button
                className="w-full text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
                onClick={() => navigate('/admin/reports')}
              >
                <div className="flex items-center">
                  <span className="text-2xl mr-3">📊</span>
                  <span>Voir les rapports</span>
                </div>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Activité récente
            </h2>
            <div className="space-y-3">
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-2xl mr-3">🎲</span>
                <div>
                  <p className="font-medium">Nouveau pari placé</p>
                  <p className="text-sm text-gray-600">Il y a 5 minutes</p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-2xl mr-3">👤</span>
                <div>
                  <p className="font-medium">Nouvel utilisateur inscrit</p>
                  <p className="text-sm text-gray-600">Il y a 15 minutes</p>
                </div>
              </div>
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <span className="text-2xl mr-3">🐓</span>
                <div>
                  <p className="font-medium">Combat terminé</p>
                  <p className="text-sm text-gray-600">Il y a 1 heure</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 